/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Button, Grid } from '@mui/material';
import { Formik } from 'formik';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'modules/common/helpers/object';
import ERROR_TYPES from 'modules/common/constants/error-types';
import queryBoxValidation from 'modules/hotel-set/validation/query-validation';
import { Alert, TextField } from 'modules/hotel-set/style';
import { LABELS } from 'modules/hotel-set/constants';
import { selectHotelGroupQuery, selectSchemaMapping } from 'modules/hotel-set/selectors';
import { hotelSetActions } from 'modules/hotel-set/slice';
import { dataHandler } from '@phg/advanced-querying-module';
import { decode } from 'html-entities';

/** Implementation to categorize Group A and Group B based on user input query
 * @param {Array} breakdownSubCategories - List of breakdown sub categories
 * @param {Function} setGroup1 - Function to set Group A values
 * @param {Function} setGroup2 - Function to set Group B values
 * @param {Function} onClose - Function to trigger on close event
 * @param {Boolean} open - To open/ close dialog box
 * @param {Boolean} isQueryEnabled - To enable/disable advanced querying feature
 * @param {Boolean} loadFilter - To load saved filter
 * @param {Function} setLoadFilter  - Function to set load filter true when the saved filter is clicked to be loaded
 * @returns
 */

const QueryBox = ({
  setGroupList,
  open = true,
  isQueryEnabled,
  loadFilter,
  setLoadFilter,
  focusOnList,
  setTemGroupList,
}) => {
  const dispatch = useDispatch();

  // setA Query and set B Query values are selected from store here. Refer each selector function for details
  const hotelGroupQuery = useSelector(selectHotelGroupQuery);
  const hotelMappingSchema = useSelector(selectSchemaMapping);
  //
  const initialValues = {
    setHotelGroup: '',
  };
  const [hotelGroupNotification, setHotelGroupNotification] = useState({ type: '', message: '' });

  // To preview values for Group A and B based on the input query
  const previewValues = (setAQuery = hotelGroupQuery) => {
    let setGroup = [];
    setGroupList([]);
    //
    if (setAQuery) {
      try {
        setGroup = dataHandler(decode(setAQuery), focusOnList, hotelMappingSchema);
        if (!isEmpty(setGroup)) {
          setGroupList(setGroup);
          setTemGroupList(setGroup);
          setHotelGroupNotification({ type: '', message: '' });
        } else {
          setHotelGroupNotification({ type: 'error', message: 'No match found for Hotel Group' });
        }
      } catch (e) {
        setHotelGroupNotification({ type: 'error', message: e.message });
      }
    }
    setLoadFilter(false);
  };

  // Triggered when load filter value changes
  useEffect(() => {
    // Call preview values function if loadFilter is  true to display selected filter values in transfer list
    if (loadFilter) previewValues();
  }, [loadFilter]);
  //
  return (
    open && (
      <Formik
        initialValues={initialValues}
        validationSchema={queryBoxValidation}
        onSubmit={() => previewValues()}
        enableReinitialize
      >
        {({ errors, handleBlur, handleSubmit, setFieldValue, touched, resetForm }) => (
          <form noValidate onSubmit={handleSubmit}>
            {hotelGroupNotification?.type === ERROR_TYPES.ERROR && hotelGroupNotification?.message && (
              <Alert mt={2} mb={3} severity={hotelGroupNotification?.type}>
                {hotelGroupNotification?.message}
              </Alert>
            )}
            <TextField
              multiline
              name="setHotelGroup"
              value={hotelGroupQuery}
              error={Boolean(touched.setHotelGroup && errors.setHotelGroup)}
              helperText={touched.setHotelGroup && errors.setHotelGroup}
              fullWidth
              onBlur={handleBlur}
              onChange={(event) => {
                setFieldValue('setHotelGroup', event.target.value);
                dispatch(hotelSetActions.setHotelGroupQuery(event.target.value));
              }}
              my={2}
              label={LABELS.GROUP_HOTEL}
              size="small"
              disabled={!isQueryEnabled}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: 2,
                  height: 'fit-content',
                },
              }}
            />
            <Grid container direction="row" justifyContent="flex-end">
              <Grid item mx={3}>
                <Button
                  type="reset"
                  onClick={() => {
                    resetForm();
                    dispatch(hotelSetActions.setHotelGroupQuery(''));
                  }}
                  disabled={!isQueryEnabled}
                >
                  Clear
                </Button>
              </Grid>
              <Grid item>
                <Button type="submit" disabled={!isQueryEnabled}>
                  Preview
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    )
  );
};
//
export default QueryBox;
